import { Modal, Tooltip } from "antd";
import moment from "moment";
import "./UserInfo.scss";

const EMPTY = "—";

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/* the API sends roles as single lowercase words ("teamlead") */
const ROLE_LABELS = {
  teamlead: "Team Lead",
  fieldagent: "Field Agent",
  telecaller: "Telecaller",
  supervisor: "Supervisor",
  admin: "Admin",
};

const formatRole = (role) => {
  const key = String(role || "").replace(/[\s_]/g, "").toLowerCase();
  return ROLE_LABELS[key] || role || "";
};

const formatDate = (date) => {
  const parsed = moment(date);
  return date && parsed.isValid() ? parsed.format("DD MMM YYYY") : EMPTY;
};

/* A list of tags that stays on one line: the first few render as chips and
   everything past them collapses into a hoverable "+N more". */
const ChipList = ({ items, max = 3, tone = "" }) => {
  if (!items?.length) return <span className="value is-empty">{EMPTY}</span>;

  const shown = items.slice(0, max);
  const hidden = items.slice(max);

  return (
    <div className="chipList">
      {shown.map((item) => (
        <span className={`chip ${tone}`} key={item}>
          {item}
        </span>
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.join(", ")}>
          <span className="chip chip--more">+{hidden.length} more</span>
        </Tooltip>
      )}
    </div>
  );
};

const UserInfo = ({ open, onClose, userDetails }) => {
  const teamLeads = userDetails?.team_lead_names || [];
  const areacodes =
    userDetails?.operational_area?.map((area) => area.areacode) || [];

  const isActive = userDetails?.status === "Active";

  const personalDetails = [
    { label: "Phone", value: userDetails?.mobile_no },
    { label: "Date of Joining", value: formatDate(userDetails?.date_of_joining) },
    { label: "Status", value: userDetails?.status },
  ];

  /* the three counters read as figures, so they get their own tiles rather
     than sitting in the label/value grid */
  const metrics = [
    { label: "Visits This Month", value: userDetails?.visits_this_month },
    { label: "Loan Cases Assigned", value: userDetails?.cases_assigned },
    {
      label: "Collection Success Rate",
      value: userDetails?.collection_success_rate,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable
      destroyOnClose
      title="All Details"
      className="viewDetailsModal"
    >
      {/* identity band — name, email and the two pills that used to be buried
          in the detail rows */}
      <div className="userIdentity">
        <div className="userIdentity__avatar" aria-hidden="true">
          {initialsOf(userDetails?.full_name) || "NA"}
        </div>
        <div className="userIdentity__text">
          <h3>{userDetails?.full_name || EMPTY}</h3>
          <p>{userDetails?.email || EMPTY}</p>
        </div>
        <div className="userIdentity__pills">
          {userDetails?.role && (
            <span className="pill pill--role">{formatRole(userDetails.role)}</span>
          )}
          {userDetails?.status && (
            <span className={`pill ${isActive ? "pill--active" : "pill--inactive"}`}>
              <i className="dot" />
              {userDetails.status}
            </span>
          )}
        </div>
      </div>

      <section className="detailsCard">
        <h4 className="detailsCard__title">Personal Information</h4>
        <div className="detailsGrid">
          {personalDetails.map(({ label, value }) => (
            <div className="detailItem" key={label}>
              <span className="label">{label}</span>
              <span className={`value ${value ? "" : "is-empty"}`}>
                {value || EMPTY}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="detailsCard">
        <h4 className="detailsCard__title">Field Activity</h4>

        <div className="detailsGrid detailsGrid--wide">
          <div className="detailItem">
            <span className="label">Assigned Team Lead</span>
            <ChipList items={teamLeads} />
          </div>
          <div className="detailItem">
            <span className="label">Operational Zone</span>
            <ChipList items={areacodes} tone="chip--zone" />
          </div>
        </div>

        <div className="metricRow">
          {metrics.map(({ label, value }) => (
            <div className="metricTile" key={label}>
              <span className={`metricTile__value ${value == null || value === "" ? "is-empty" : ""}`}>
                {value == null || value === "" ? EMPTY : value}
              </span>
              <span className="metricTile__label">{label}</span>
            </div>
          ))}
        </div>
      </section>
    </Modal>
  );
};

export default UserInfo;
